import moment from "moment/min/moment-with-locales";
import { useMemo, useRef, useState } from "react";
import { Col, Row, Stack } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import { useFetchData, useLatestCalculatedDate, useResolvedModules, useSetDataTypeOnStationChange } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import AverageTypePicker from "../components/Pickers/AverageTypePicker";
import DataTypePicker from "../components/Pickers/DataTypePicker";
import DatesPicker, { latestFull2Weeks, latestFullMonth, latestFullQuarter, latestFullWeek } from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import StationPicker from "../components/Pickers/StationPicker";
import { useUserFormState } from "../components/UserSelection";
import BreaksCard from "../components/health/BreaksCard";
import ContentBalanceCard from "../components/health/ContentBalanceCard";
import DevicesCard from "../components/health/DevicesCard";
import GenderCard from "../components/health/GenderCard";
import ListenerGroupsCard from "../components/health/ListenerGroupsCard";
import PPMDemographicsCard from "../components/health/PPMDemographicsCard";
import RatingsCard from "../components/health/RatingsCard";
import ReachCard from "../components/health/ReachCard";
import SongsCard from "../components/health/SongsCard";
import TSLCard from "../components/health/TSLCard";
import TotalSessionsCard from "../components/health/TotalSessionsCard";
import { DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage } from "../components/pageSections";

export default function Health() {
    const moduleName = useMemo(() => "health", []);
    const moduleId = useMemo(() => ({ 1: 21, 2: 21 }), []);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName);

    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);

    const [daypartLoading, daypartErrorCode, daypartData, fetchDaypartData] = useFetchData();
    const [allTimeRatingsLoading, allTimeRatingsErrorCode, allTimeRatingsData, fetchAllTimeRatingsData] = useFetchData();
    const [contentBalanceLoading, contentBalanceErrorCode, contentBalanceData, fetchContentBalanceData] = useFetchData();

    const anyLoading = daypartLoading || allTimeRatingsLoading || contentBalanceLoading;
    const allErrorCode = daypartErrorCode && allTimeRatingsErrorCode && contentBalanceErrorCode;
    const anyData = daypartData || contentBalanceData || allTimeRatingsData;

    const resolvedModules = useResolvedModules();
    const ratingsEnabled = useMemo(() => resolvedModules?.includes(2), [resolvedModules]);
    const musicEnabled = useMemo(() => resolvedModules?.includes(5), [resolvedModules]);

    const container = useRef(null);
    const [fileName, setFileName] = useState("Health");

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            if (formData.dateRange === undefined) {
                setFormData(current => ({ ...current, dateRange: latestFullWeek(latestCalculatedDate, true) }));
            }
            return [
                latestFullWeek(latestCalculatedDate),
                latestFull2Weeks(latestCalculatedDate),
                latestFullMonth(latestCalculatedDate),
                latestFullQuarter(latestCalculatedDate),
            ];
        }
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    useSetDataTypeOnStationChange(setFormData, formData.station);
    const demo = useMemo(
        () => [{ data_type: formData.dataType, listener_group: [363, 130, 505].includes(formData.station[0]?.radio_id) ? 0 : 1 }],
        [formData.dataType, formData.station]
    );
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, demo);

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const startDate = moment(formData.dateRange[0]);
        const endDate = moment(formData.dateRange[1]);

        const isSameDay = startDate.isSame(endDate, "day");

        const input = {
            daypart_id: formData.daypart[0].daypart_id,
            data_type: formData.dataType,
            start_datetime: startDate.format("YYYY-MM-DDTHH:mm:ss"),
            end_datetime: isSameDay ? startDate.format("YYYY-MM-DDT23:59:59") : endDate.format("YYYY-MM-DDTHH:mm:ss"),
            last_year_averages: formData.lastYearAverages,
        };

        setFileName(
            `Health ${formData.station[0].radio_name} from ${moment(formData.dateRange[0]).format("DD-MM-YYYY")} to ${moment(formData.dateRange[1]).format(
                "DD-MM-YYYY"
            )}`
        );

        await Promise.all([
            fetchDaypartData("/health/", input),
            fetchAllTimeRatingsData(`/health/ratings/all_time_peak/?daypart_id=${formData.daypart[0].daypart_id}&data_type=${formData.dataType}`),
            fetchContentBalanceData("/health/content_balance/", input),
        ]);
    }

    return (
        <ModulePage>
            <ModulePage.Title>Health</ModulePage.Title>
            <ModulePage.InputSelection
                handleFormSubmit={handleFormSubmit}
                disabled={!formData.station.length || !formData.dateRange || !formData.daypart.length}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        moduleFilterFunction={x => x.module_id === 21}
                        loadOnChange={loadFormData}
                    />
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <AverageTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <DataTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent errorCode={allErrorCode}>
                <DataContent.Placeholder show={!anyLoading && !anyData}>
                    <PageDesciption
                        title={"Health"}
                        whatList={[
                            "Quick view of how the station is performing now vs. your average",
                            "A great place to spot large changes in your listening behaviour or content",
                            "Over or underperforming KPIs are easy to spot at a glance",
                        ]}
                        knowList={[
                            { text: "You can choose to view last 3 months or last year average for comparison" },
                            { text: "If something is unclear check the tooltip on top of the header" },
                            { text: "Stream and PPM data will show you slightly different views based on available data" },
                        ]}
                        relatedLinks={[
                            {
                                title: "An Apple a Day",
                                url: "https://radioanalyzer.com/blog/an-apple-a-day",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/qscOOTBStkE?si=TL7L4Lg-AxsIVJ4X"
                    />
                </DataContent.Placeholder>
                {anyData && (
                    <div ref={container}>
                        <DescriptionWithDownloadRow screenshotContainer={container} filename={fileName} data={anyData}>
                            <DataDescription>
                                <DataDescription.Station radioId={anyData?.radio_id} />
                                <DataDescription.DateRange startDate={anyData?.start_datetime} endDate={anyData?.end_datetime} />
                                <DataDescription.TimeRange startTime={anyData?.start_time} endTime={anyData?.end_time} />
                                <DataDescription.Perspective days={anyData?.days} />
                                <DataDescription.Simple label="Average" value={anyData?.last_year_averages ? "Last Year" : "Last 13 Weeks"} />
                                <DataDescription.ExcludedDays excludedDays={anyData?.excluded_days} />
                            </DataDescription>
                            {daypartData && daypartData?.data_type === 1 && !daypartData.is_full_daypart && (
                                <Stack direction="horizontal">
                                    <span className=" d-inline-flex align-items-center">
                                        <span className="health-daypart-legend me-1"></span> Values with a green header are calculated for the entire daypart on
                                        a weekly average
                                    </span>
                                </Stack>
                            )}
                        </DescriptionWithDownloadRow>
                        <Row>
                            {ratingsEnabled && (
                                <Col xs={12} lg={6}>
                                    <Row className="g-3">
                                        {(daypartLoading || daypartData) && (
                                            <>
                                                <Col xs={12} className="order-first">
                                                    <TSLCard
                                                        data={{ tsl_session: daypartData?.tsl_session, tsl_listener: daypartData?.tsl_listener }}
                                                        loading={daypartLoading}
                                                        error={daypartErrorCode}
                                                        isFullDaypart={daypartData?.data_type === 2 || daypartData?.is_full_daypart}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6} xxl={3} className=" order-0">
                                                    <RatingsCard
                                                        data={daypartData?.ratings}
                                                        loading={daypartLoading}
                                                        error={daypartErrorCode}
                                                        allTimeData={allTimeRatingsData}
                                                        allTimeLoading={allTimeRatingsLoading}
                                                        allTimeError={allTimeRatingsErrorCode}
                                                    />
                                                </Col>
                                                {((daypartLoading && formData.dataType === 1) || daypartData?.data_type === 1) && (
                                                    <Col xs={12} xxl={9} className=" order-1 order-xxl-0">
                                                        <DevicesCard
                                                            data={daypartData?.mobility}
                                                            loading={daypartLoading}
                                                            error={daypartErrorCode}
                                                            isFullDaypart={daypartData?.data_type === 2 || daypartData?.is_full_daypart}
                                                        />
                                                    </Col>
                                                )}
                                                {((daypartLoading && formData.dataType === 2) || daypartData?.data_type === 2) && (
                                                    <Col xs={12} xxl={9} className=" order-1 order-xxl-0">
                                                        <PPMDemographicsCard
                                                            data={daypartData?.ppm_demographics}
                                                            loading={daypartLoading}
                                                            error={daypartErrorCode}
                                                        />
                                                    </Col>
                                                )}
                                                {((daypartLoading && formData.dataType === 1) || daypartData?.data_type === 1) && (
                                                    <Col xs={12} sm={6} xxl={3} className="order-0 order-xxl-1">
                                                        <TotalSessionsCard
                                                            data={{ reach: daypartData?.reach, total_sessions: daypartData?.total_sessions }}
                                                            loading={daypartLoading}
                                                            error={daypartErrorCode}
                                                        />
                                                    </Col>
                                                )}
                                                {((daypartLoading && formData.dataType === 2) || daypartData?.data_type === 2) && (
                                                    <Col xs={12} sm={6} xxl={3} className=" order-0 order-xxl-1">
                                                        <ReachCard data={daypartData?.reach} loading={daypartLoading} error={daypartErrorCode} />
                                                    </Col>
                                                )}
                                                <Col xs={12} xxl={9} className=" order-2">
                                                    <ListenerGroupsCard
                                                        data={daypartData?.listener_groups}
                                                        loading={daypartLoading}
                                                        error={daypartErrorCode}
                                                        isFullDaypart={daypartData?.data_type === 2 || daypartData?.is_full_daypart}
                                                    />
                                                </Col>
                                                {((daypartLoading && formData.dataType === 2) || daypartData?.data_type === 2) && (
                                                    <Col xs={12} className="order-2">
                                                        <GenderCard data={daypartData?.gender} loading={daypartLoading} error={daypartErrorCode} />
                                                    </Col>
                                                )}
                                            </>
                                        )}
                                    </Row>
                                </Col>
                            )}
                            {musicEnabled && (
                                <Col xs={12} lg={6}>
                                    <Row className="g-3">
                                        {(contentBalanceLoading || contentBalanceData) && (
                                            <Col xs={12}>
                                                <SongsCard data={contentBalanceData} loading={contentBalanceLoading} error={contentBalanceErrorCode} />
                                            </Col>
                                        )}
                                        {(contentBalanceLoading || contentBalanceData) && (
                                            <Col xs={12}>
                                                <ContentBalanceCard data={contentBalanceData} loading={contentBalanceLoading} error={contentBalanceErrorCode} />
                                            </Col>
                                        )}
                                        {(contentBalanceLoading || contentBalanceData) && (
                                            <Col xs={12}>
                                                <BreaksCard data={contentBalanceData} loading={contentBalanceLoading} error={contentBalanceErrorCode} />
                                            </Col>
                                        )}
                                    </Row>
                                </Col>
                            )}
                        </Row>
                    </div>
                )}
            </ModulePage.DataContent>
        </ModulePage>
    );
}
